import { BTText } from "@btnet/core-components/BTText";
import { cn } from "@btnet/utilities-core";

import type { SearchDialogEnterAction } from "./searchDialog.types";

interface ISearchDialogFooterProps {
    /** What a bare Enter does now, from `resolveEnterAction`. */
    enterAction: SearchDialogEnterAction;
    /** Whether there is a query for the modifier to send. */
    canAsk: boolean;
    /** Whether an inline guess is showing, which Tab accepts. */
    canAccept?: boolean;
    /** Sits at the right edge, opposite the key hints. */
    feedback: React.ReactNode;
}

/** A key as the search surfaces show one. */
export function Kbd({ children, className }: { children: React.ReactNode; className?: string }) {
    return (
        <kbd
            className={cn(
                "rounded-xs border border-default bg-surface-base px-1 text-fg-secondary",
                className,
            )}
        >
            {children}
        </kbd>
    );
}

/** Keys and label in one face and size, so the glyphs don't fall back to the system font. */
export function Hint(props: { keys: string[]; label: string }) {
    return (
        <span className="flex items-center gap-1">
            {props.keys.map((k) => (
                <BTText key={k} size="normal-md" tag="span" className="text-fg-tertiary">
                    {k}
                </BTText>
            ))}
            <BTText size="normal-md" tag="span" className="text-fg-tertiary">
                {props.label}
            </BTText>
        </span>
    );
}

export function SearchDialogFooter({
    enterAction,
    canAsk,
    canAccept = false,
    feedback,
}: ISearchDialogFooterProps) {
    return (
        <div
            className="flex shrink-0 items-center justify-between gap-4 border-t border-default px-4 py-2 max-sm:justify-end"
            data-testid="search-dialog-footer"
        >
            {/* Key hints mean nothing on touch, so they hide below `sm`; the
                feedback link stays. Not `hidden sm:flex`: webforms CSS has a
                global `.hidden{display:none!important}`. */}
            <span className="flex items-center gap-4 max-sm:hidden">
                {canAccept && <Hint keys={["tab"]} label="accept" />}
                <Hint keys={["↑", "↓"]} label="move" />
                {/* Only what would actually happen: an inert Enter is not hinted. */}
                {enterAction !== "none" && <Hint keys={["↵"]} label={enterAction} />}
                {/* Ctrl, not the Mac glyph: the handler takes either modifier,
                    and these are Windows users. Not hinted when Enter already
                    asks, or when there is nothing to send. */}
                {canAsk && enterAction !== "ask" && <Hint keys={["ctrl", "↵"]} label="ask" />}
                <Hint keys={["esc"]} label="close" />
            </span>
            {feedback}
        </div>
    );
}
